<script setup lang="ts">
/**
 * AssistantWidget — global floating AI-marketer launcher + slide-over (FIX-AI-6).
 *
 * A fixed ✨ button sits bottom-right on every page; clicking it (or ⌥⌘A) opens
 * a slide-over that renders the full AiAssistantPage in `embedded` mode, so the
 * assistant follows the operator across pages instead of being a single menu
 * item. The conversation lives in useAssistantStore (persisted), so it survives
 * navigation and reload; collapsing the panel keeps the launcher in place.
 */
import { onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { Sparkles, X } from "lucide-vue-next";
import { useAssistantStore } from "../../stores/assistant";
import { useContextualAssistant } from "../../composables/useContextualAssistant";
import AiAssistantPage from "../../pages/AiAssistantPage.vue";

const assistant = useAssistantStore();
const { open } = storeToRefs(assistant);

// Замеч.4: если страница зарегистрировала контекстного помощника (напр. страница
// бота — BotAssistantChat), ✨ открывает ЕГО, а не общий копайлот.
const ctx = useContextualAssistant();
const launchTitle = computed(() => ctx.current.value?.label ?? "AI-маркетолог (⌥⌘A)");

// B11 — на страницах-холстах (бот/сценарий/кампания) правый-нижний угол занят
// миникартой + зум-панелью, а у самих страниц есть своя AI-панель. Прячем глобальный
// ✨-FAB на этих роутах, чтобы он не перекрывал зум.
const route = useRoute();
// Все роуты холстов-редакторов (BotEditPage / JourneyEditPage / CampaignEditPage),
// включая создание НОВОЙ сущности (journey-new/campaign-new) — у них тот же холст с
// зум-панелью в правом-нижнем углу и своя AI-панель.
const EDITOR_ROUTES = new Set([
  "bot-edit", "journey-edit", "journey-new", "campaign-edit", "campaign-new",
]);
const hideLauncher = computed(() => EDITOR_ROUTES.has(String(route.name ?? "")));
function launch(): void {
  if (ctx.current.value) ctx.current.value.open();
  else assistant.toggle();
}

// ⌥⌘A (Mac) / Ctrl+Alt+A — toggle. e.code stays "KeyA" even when Alt remaps the
// printed character on macOS.
function onKey(e: KeyboardEvent): void {
  if ((e.metaKey || e.ctrlKey) && e.altKey && e.code === "KeyA") {
    e.preventDefault();
    launch();
  }
}
onMounted(() => window.addEventListener("keydown", onKey));
onBeforeUnmount(() => window.removeEventListener("keydown", onKey));
</script>

<template>
  <!-- Floating launcher — fixed bottom-right; скрыт на холстах-редакторах (B11). -->
  <button
    v-if="!hideLauncher"
    type="button"
    class="fixed bottom-5 right-5 z-40 size-12 rounded-full bg-accent-600 hover:bg-accent-700 text-white shadow-lg shadow-accent-600/30 flex items-center justify-center transition-colors"
    :title="launchTitle"
    :aria-label="launchTitle"
    data-testid="assistant-launcher"
    @click="launch()"
  >
    <Sparkles class="size-5" />
  </button>

  <!-- Slide-over panel -->
  <Transition
    enter-active-class="transition duration-150 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-100 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="open" class="fixed inset-0 z-50 flex justify-end" data-testid="assistant-panel">
      <div class="absolute inset-0 bg-surface-900/30" @click="assistant.hide()"></div>
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
        appear
      >
        <div class="relative w-full max-w-xl bg-surface-0 shadow-2xl flex flex-col h-full">
          <header class="flex items-center justify-between px-4 py-3 border-b border-surface-200 shrink-0">
            <div class="flex items-center gap-2">
              <Sparkles class="size-5 text-accent-600" />
              <span class="font-semibold text-surface-900">AI-маркетолог</span>
            </div>
            <button
              type="button"
              class="text-surface-400 hover:text-surface-700"
              aria-label="Свернуть"
              data-testid="assistant-close"
              @click="assistant.hide()"
            >
              <X class="size-5" />
            </button>
          </header>
          <div class="flex-1 min-h-0 overflow-hidden px-4 py-3">
            <AiAssistantPage embedded />
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>
